import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IDeviceChannel } from 'lavva.exalushome/build/js/Services/Devices/IDeviceChannel';
import { groupBy } from 'lodash';
import { EmptyStateBox } from '../../../../../components/empty-state-box';
import { useDebounce } from '../../../../../hooks/debounce/use-debounce';
import { ActionCreateDeviceVariants } from '../../../../../modules/action-create/device-variants';
import { ROUTES } from '../../../../../routes/routes';
import { useChannels } from '../../channel-list/hooks/use-channels';
import { DeviceChannelsGrouped } from '../../group/components/grouped-channels/grouped-channel-box';
import { DeviceChannelsCheck } from '../../group/types';
import { useExalusCreateActionContext } from '../context';
import { useActionTaskVariants } from '../hooks/use-action-task-variants';

const ExalusActionCreateDeviceVariants: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('action');
  const [devicesChannelsGrouped, setDevicesChannelsGrouped] = useState<DeviceChannelsCheck[]>([]);
  const { taskVariant, setChannels } = useExalusCreateActionContext();
  const { getChannelsByVariant } = useActionTaskVariants();
  const { channels } = useChannels();
  const { inputValue, inputQuery, setInputValue } = useDebounce();
  const [selectedChannels, setSelectedChannels] = useState<IDeviceChannel[]>([]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => setInputValue(event.target.value);

  useEffect(() => {
    const channelList = getChannelsByVariant(channels || []).filter((x) =>
      x.Name.toLowerCase().includes(inputQuery.toLowerCase()),
    );
    const grouped: Record<string, IDeviceChannel[]> = groupBy(
      channelList.filter((x) => !x.GetDevice().ShouldChannelsBeGrouped),
      (x) => x.GetDevice().Guid,
    );

    setDevicesChannelsGrouped(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      Object.entries(grouped).map(([_, channels]) => ({
        device: channels[0].GetDevice(),
        channels: channels.map((ch) => ({
          id: ch.ChannelId,
          channel: ch,
          checked: selectedChannels.some((x) => x.ChannelId === ch.ChannelId),
        })),
      })),
    );
  }, [taskVariant, channels, inputQuery, selectedChannels]);

  const handleNext = () => {
    setChannels(selectedChannels);
    navigate(ROUTES.ActionChannelSettings());
  };

  const handleChannelOnChange = useCallback(
    (id: string) => {
      const channel = devicesChannelsGrouped.flatMap((x) => x.channels).find((x) => x.id === id);

      if (channel) {
        const foundIndex = selectedChannels.findIndex((x) => x.ChannelId === channel.channel.ChannelId);

        if (foundIndex === -1) {
          setSelectedChannels([...selectedChannels, channel.channel]);
        } else {
          setSelectedChannels(selectedChannels.filter((_, index) => index !== foundIndex));
        }
      }
    },
    [devicesChannelsGrouped, selectedChannels],
  );

  return (
    <ActionCreateDeviceVariants handleNext={handleNext} searchInput={inputValue} handleChange={handleChange}>
      {devicesChannelsGrouped.length ? (
        <ul className="grid-list-24">
          {devicesChannelsGrouped.map((deviceChannels: DeviceChannelsCheck) => (
            <li key={deviceChannels.device.Guid}>
              <DeviceChannelsGrouped
                deviceChannels={deviceChannels}
                groupLabel={deviceChannels.device.Name}
                handleChannelOnChange={handleChannelOnChange}
              />
            </li>
          ))}
        </ul>
      ) : (
        <EmptyStateBox content={t('noChannels')} />
      )}
    </ActionCreateDeviceVariants>
  );
};

export default ExalusActionCreateDeviceVariants;
